import { useMemo } from 'react';
import { CardStatsSectionProps, CardStatItem, CardStatTextItem } from '@/shadcn/components/card-overlay';

export interface UseCardStatsSectionOptions {
  /** Array of custom stat items */
  stats?: CardStatItem[];
  /** Array of custom text items */
  textItems?: CardStatTextItem[];
  /** Show date with calendar icon */
  showDate?: boolean;
  /** Date value */
  date?: string | Date | null;
  /** Show impressions stat */
  showImpressions?: boolean;
  /** Impressions value */
  impressions?: number | null;
  /** Show engagements stat */
  showEngagements?: boolean;
  /** Engagements value */
  engagements?: number | null;
  /** Show followers stat */
  showFollowers?: boolean;
  /** Followers value */
  followers?: number | null;
  /** Layout style */
  layout?: 'space-between' | 'flex-end' | 'flex-start' | 'center';
  /** Additional CSS classes */
  className?: string;
  /** Custom children */
  children?: React.ReactNode;
}

export interface UseCardStatsSectionResult {
  /** Props for CardStatsSection component */
  cardStatsSectionProps: CardStatsSectionProps;
  /** Whether any stats should be displayed */
  hasStats: boolean;
}

/**
 * useCardStatsSection
 * -------------------
 * Hook to build props for CardStatsSection component.
 * Provides common stat configurations and validation logic.
 *
 * @param options - Configuration options for stats section
 * @returns Object with cardStatsSectionProps and hasStats flag
 */
export const useCardStatsSection = (
  options: UseCardStatsSectionOptions = {}
): UseCardStatsSectionResult => {
  const {
    stats = [],
    textItems = [],
    showDate = false,
    date,
    showImpressions = false,
    impressions,
    showEngagements = false,
    engagements,
    showFollowers = false,
    followers,
    layout = 'space-between',
    className,
    children,
  } = options;

  const cardStatsSectionProps = useMemo<CardStatsSectionProps>(() => ({
    stats,
    textItems,
    showDate,
    date,
    showImpressions,
    impressions,
    showEngagements,
    engagements,
    showFollowers,
    followers,
    layout,
    className,
    children,
  }), [
    stats,
    textItems,
    showDate,
    date,
    showImpressions,
    impressions,
    showEngagements,
    engagements,
    showFollowers,
    followers,
    layout,
    className,
    children,
  ]);

  const hasStats = useMemo(() => {
    return (
      stats.length > 0 ||
      textItems.length > 0 ||
      (showDate && Boolean(date)) ||
      (showImpressions && impressions !== null) ||
      (showEngagements && engagements !== null) ||
      (showFollowers && followers !== null) ||
      Boolean(children)
    );
  }, [
    stats.length,
    textItems.length,
    showDate,
    date,
    showImpressions,
    impressions,
    showEngagements,
    engagements,
    showFollowers,
    followers,
    children,
  ]);

  return {
    cardStatsSectionProps,
    hasStats,
  };
};

/**
 * useContentPreviewStats
 * ----------------------
 * Preset hook for content preview cards with date and standard stats.
 */
export const useContentPreviewStats = (
  date?: string | Date | null,
  impressions?: number | null,
  engagements?: number | null,
  layout: 'space-between' | 'flex-end' | 'flex-start' | 'center' = 'space-between'
): UseCardStatsSectionResult => {
  return useCardStatsSection({
    showDate: true,
    date,
    showImpressions: true,
    impressions,
    showEngagements: true,
    engagements,
    layout,
  });
};

/**
 * useSimilarCreatorsStats
 * -----------------------
 * Preset hook for similar creators cards with followers and engagements (no date).
 */
export const useSimilarCreatorsStats = (
  followers?: number | null,
  engagements?: number | null,
  layout: 'space-between' | 'flex-end' | 'flex-start' | 'center' = 'flex-end'
): UseCardStatsSectionResult => {
  return useCardStatsSection({
    showFollowers: true,
    followers,
    showEngagements: true,
    engagements,
    layout,
  });
};

/**
 * useTopPostStats
 * ---------------
 * Preset hook for top post cards with date and performance stats.
 */
export const useTopPostStats = (
  date?: string | Date | null,
  impressions?: number | null,
  engagements?: number | null
): UseCardStatsSectionResult => {
  return useCardStatsSection({
    showDate: true,
    date,
    showImpressions: true,
    impressions,
    showEngagements: true,
    engagements,
    layout: 'space-between',
  });
};

/**
 * useAnalyticsCardStats
 * ---------------------
 * Preset hook for analytics cards with impressions and engagements only.
 */
export const useAnalyticsCardStats = (
  impressions?: number | null,
  engagements?: number | null,
  layout: 'space-between' | 'flex-end' | 'flex-start' | 'center' = 'flex-end'
): UseCardStatsSectionResult => {
  return useCardStatsSection({
    showImpressions: true,
    impressions,
    showEngagements: true,
    engagements,
    layout,
  });
};
